package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.requiredWidth
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.model.AttendanceStatus
import ru.chm.gradereport.model.GradeSheetDto
import ru.chm.gradereport.model.HomeworkDto
import ru.chm.gradereport.model.LessonSessionDto
import ru.chm.gradereport.ui.components.ConfirmDialog
import ru.chm.gradereport.ui.components.HorizontalScrollBar
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.formatDisplayDate
import ru.chm.gradereport.ui.components.softClickable
import ru.chm.gradereport.ui.theme.BlueAccent
import ru.chm.gradereport.ui.theme.GradeTheme

private sealed class PendingDelete {
    data class Session(val id: String, val label: String) : PendingDelete()
    data class Homework(val id: String, val title: String) : PendingDelete()
}

@Composable
fun GradeSheetTable(
    sheet: GradeSheetDto,
    editable: Boolean,
    isAdmin: Boolean = false,
    formsBearerConfigured: Boolean = false,
    modifier: Modifier = Modifier,
    onCycleAttendance: (String, String, AttendanceStatus?) -> Unit = { _, _, _ -> },
    onSetGrade: (String, String, Double?) -> Unit = { _, _, _ -> },
    onOpenStats: (String) -> Unit,
    onAddSession: (String, String, String) -> Unit = { _, _, _ -> },
    onUpdateSession: (String, String, String, String) -> Unit = { _, _, _, _ -> },
    onDeleteSession: (String) -> Unit = {},
    onAddHomework: (sessionId: String, title: String, description: String, maxScore: Double) -> Unit =
        { _, _, _, _ -> },
    onUpdateHomework: (
        homeworkId: String,
        sessionId: String,
        title: String,
        description: String,
        maxScore: Double,
    ) -> Unit = { _, _, _, _, _ -> },
    onDeleteHomework: (String) -> Unit = {},
    onUpdateSubmission: (submissionId: String, url: String) -> Unit = { _, _ -> },
    onDeleteSubmission: (submissionId: String) -> Unit = {},
    onCheckInQrClick: () -> Unit = {},
) {
    var showAddSession by remember { mutableStateOf(false) }
    var showAddHomework by remember { mutableStateOf(false) }
    var showSubmissions by remember { mutableStateOf(false) }
    var viewingHomework by remember { mutableStateOf<HomeworkDto?>(null) }
    var viewingSession by remember { mutableStateOf<LessonSessionDto?>(null) }
    var selectedSessionId by remember(sheet.sessions) {
        mutableStateOf(sheet.sessions.lastOrNull()?.id)
    }
    var gradeEdit by remember { mutableStateOf<GradeEditTarget?>(null) }
    var pendingDelete by remember { mutableStateOf<PendingDelete?>(null) }
    var attendanceEdit by remember { mutableStateOf<AttendanceEditTarget?>(null) }
    var quickEditKey by remember { mutableStateOf<Pair<String, String>?>(null) }
    var localAttendance by remember {
        mutableStateOf<Map<Pair<String, String>, AttendanceStatus?>>(emptyMap())
    }
    var sortOrder by remember { mutableStateOf(SessionSortOrder.NewestFirst) }
    var focusDate by remember { mutableStateOf<String?>(null) }

    val attendanceMap = remember(sheet) {
        sheet.attendance.associateBy { it.studentId to it.sessionId }
    }
    LaunchedEffect(sheet.attendance) {
        localAttendance = localAttendance.filter { (key, local) ->
            attendanceMap[key]?.status != local
        }
    }
    val gradeMap = remember(sheet) {
        sheet.grades.associateBy { it.studentId to it.homeworkId }
    }
    val gradeScores = remember(gradeMap) {
        gradeMap.mapValues { (_, grade) -> grade.score }
    }
    val submissionsByCell = remember(sheet.submissions) {
        sheet.submissions.groupBy { it.studentId to it.homeworkId }
    }
    val unseenSubmissionsByCell = remember(sheet.submissions) {
        sheet.submissions.filter { !it.seen }.groupBy { it.studentId to it.homeworkId }
    }
    val homeworksBySession = remember(sheet) {
        sheet.homeworks.groupBy { it.sessionId }
    }
    val orphanHomeworks = remember(sheet) {
        val sessionIds = sheet.sessions.map { it.id }.toSet()
        sheet.homeworks.filter { it.sessionId == null || it.sessionId !in sessionIds }
    }
    var filters by remember { mutableStateOf(SheetFilterState()) }
    val effectiveAttendance = remember(attendanceMap, localAttendance) {
        buildMap {
            attendanceMap.forEach { (key, dto) -> put(key, dto.status) }
            putAll(localAttendance)
        }
    }
    val visibleStudents = remember(sheet, filters, gradeScores, effectiveAttendance, focusDate) {
        filterStudents(sheet, filters, gradeScores, effectiveAttendance, focusDate)
    }
    val filterMaxGrade = remember(sheet, focusDate) { sheetMaxGradeOption(sheet, focusDate) }
    LaunchedEffect(focusDate, filterMaxGrade) {
        val g = filters.grade
        if (g is GradeFilter.Score && g.value > filterMaxGrade) {
            filters = filters.copy(grade = GradeFilter.All)
        }
    }

    val orderedSessions = remember(sheet.sessions, sortOrder) {
        sortSessions(sheet.sessions, sortOrder)
    }
    val sessionDates = remember(orderedSessions) {
        orderedSessions.map { it.date }.filter { it.isNotBlank() }.distinct()
    }
    LaunchedEffect(sessionDates, focusDate) {
        if (focusDate != null && focusDate !in sessionDates) {
            focusDate = null
        }
    }
    val displaySessions = remember(orderedSessions, focusDate) {
        if (focusDate == null) orderedSessions
        else orderedSessions.filter { it.date == focusDate }
    }
    val showOrphans = focusDate == null && orphanHomeworks.isNotEmpty()

    val hScroll = rememberScrollState()

    LaunchedEffect(focusDate, sortOrder) {
        hScroll.scrollTo(0)
    }

    Column(modifier = modifier.fillMaxWidth()) {
        if (editable) {
            SheetEditorPanel(
                canAddHomework = sheet.sessions.isNotEmpty(),
                onAddSessionClick = { showAddSession = true },
                onAddHomeworkClick = { showAddHomework = true },
                onCheckInQrClick = onCheckInQrClick,
                onSubmissionsClick = { showSubmissions = true },
            )
        }

        SheetFilterPanel(
            filters = filters,
            maxGrade = filterMaxGrade,
            visibleCount = visibleStudents.size,
            totalCount = sheet.students.size,
            visibleStudents = visibleStudents,
            showAttention = isAdmin,
            onChange = { filters = it },
        )

        if (sheet.sessions.isNotEmpty()) {
            SheetViewControls(
                sortOrder = sortOrder,
                onSortOrderChange = { sortOrder = it },
                dates = sessionDates,
                focusDate = focusDate,
                onFocusDateChange = { date -> focusDate = date },
            )
        }

        if (sheet.sessions.isEmpty()) {
            EmptySessionsBanner(editable = editable)
        }

        val markColumns = displaySessions.size +
            displaySessions.sumOf { homeworksBySession[it.id].orEmpty().size } +
            if (showOrphans) orphanHomeworks.size else 0
        val sessionGroupGaps = (displaySessions.size - 1).coerceAtLeast(0) +
            if (showOrphans && displaySessions.isNotEmpty()) 1 else 0
        val contentWidth =
            ColIndex + ColStudent + ColMark * markColumns + SessionGroupGap * sessionGroupGaps + 16.dp
        val colors = GradeTheme.colors
        val rowEven = colors.tableRowEven
        val rowOdd = colors.tableRowOdd
        val headerBg = colors.tableHeader
        val tableShape = RoundedCornerShape(12.dp)

        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .clip(tableShape)
                .border(1.dp, colors.border, tableShape)
                .background(colors.surface, tableShape),
        ) {
            val tableWidth = maxOf(contentWidth, maxWidth)
            Column(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier
                        .horizontalScroll(hScroll)
                        .width(tableWidth)
                        .background(colors.surface),
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(HeaderH)
                            .background(headerBg)
                            .padding(horizontal = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        TableLabel("№", ColIndex, colors.textMuted, 16.sp, FontWeight.Bold)
                        TableLabel("Студент", ColStudent, colors.textMuted, 16.sp, FontWeight.Bold)

                        displaySessions.forEachIndexed { sessionIndex, session ->
                            if (sessionIndex > 0) {
                                Spacer(Modifier.width(SessionGroupGap))
                            }
                            val sessionHw = homeworksBySession[session.id].orEmpty()
                                .sortedBy { it.orderIndex }
                            SessionAttendanceHeader(
                                session = session,
                                onOpen = { viewingSession = session },
                            )
                            sessionHw.forEach { hw ->
                                HomeworkHeader(
                                    homework = hw,
                                    onOpen = { viewingHomework = hw },
                                )
                            }
                        }

                        if (showOrphans && displaySessions.isNotEmpty()) {
                            Spacer(Modifier.width(SessionGroupGap))
                        }
                        if (showOrphans) {
                            orphanHomeworks.forEach { hw ->
                                HomeworkHeader(
                                    homework = hw,
                                    orphan = true,
                                    onOpen = { viewingHomework = hw },
                                )
                            }
                        }
                    }

                    if (sheet.students.isEmpty()) {
                        MutedText(
                            "Нет студентов — добавьте на вкладке «Студенты».",
                            modifier = Modifier.padding(24.dp),
                        )
                    } else if (visibleStudents.isEmpty()) {
                        MutedText(
                            "Нет студентов по выбранным фильтрам.",
                            modifier = Modifier.padding(24.dp),
                        )
                    }

                    visibleStudents.forEachIndexed { index, student ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(RowH)
                                .background(if (index % 2 == 0) rowEven else rowOdd)
                                .padding(horizontal = 8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            TableLabel("${index + 1}", ColIndex, colors.textMuted, 17.sp, FontWeight.Medium)
                            Box(
                                modifier = Modifier
                                    .requiredWidth(ColStudent)
                                    .padding(6.dp)
                                    .height(48.dp)
                                    .softClickable(
                                        onClick = { onOpenStats(student.id) },
                                        highlight = BlueAccent,
                                        shape = RoundedCornerShape(10.dp),
                                        hoverAlpha = 0.16f,
                                        pressAlpha = 0.26f,
                                    )
                                    .padding(horizontal = 8.dp),
                                contentAlignment = Alignment.CenterStart,
                            ) {
                                Text(
                                    text = student.fullName,
                                    color = BlueAccent,
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 18.sp,
                                    maxLines = 2,
                                    overflow = TextOverflow.Ellipsis,
                                )
                            }

                            displaySessions.forEachIndexed { sessionIndex, session ->
                                if (sessionIndex > 0) {
                                    Spacer(Modifier.width(SessionGroupGap))
                                }
                                val key = student.id to session.id
                                val serverStatus = attendanceMap[key]?.status
                                val status = if (key in localAttendance) {
                                    localAttendance[key]
                                } else {
                                    serverStatus
                                }
                                AttendanceCell(
                                    status = status,
                                    editable = editable,
                                    onClick = {
                                        if (status == AttendanceStatus.PENDING) {
                                            quickEditKey = null
                                            localAttendance = localAttendance + (key to AttendanceStatus.PRESENT)
                                            onCycleAttendance(
                                                student.id,
                                                session.id,
                                                AttendanceStatus.PRESENT,
                                            )
                                            return@AttendanceCell
                                        }
                                        val canQuickCycle = status == null || quickEditKey == key
                                        if (canQuickCycle) {
                                            val next = nextStatus(status)
                                            quickEditKey = key
                                            localAttendance = localAttendance + (key to next)
                                            onCycleAttendance(student.id, session.id, next)
                                        } else {
                                            quickEditKey = null
                                            val label = buildString {
                                                append(formatDisplayDate(session.date))
                                                if (session.title.isNotBlank()) {
                                                    append(" — ${session.title}")
                                                }
                                            }
                                            attendanceEdit = AttendanceEditTarget(
                                                studentId = student.id,
                                                studentName = student.fullName,
                                                sessionId = session.id,
                                                sessionLabel = label,
                                                current = status,
                                            )
                                        }
                                    },
                                )
                                homeworksBySession[session.id].orEmpty()
                                    .sortedBy { it.orderIndex }
                                    .forEach { hw ->
                                        val grade = gradeMap[student.id to hw.id]?.score
                                        GradeCell(
                                            grade = grade,
                                            hasSubmission = unseenSubmissionsByCell[student.id to hw.id]
                                                .orEmpty()
                                                .isNotEmpty(),
                                            editable = editable,
                                            onClick = {
                                                gradeEdit = GradeEditTarget(
                                                    studentId = student.id,
                                                    studentName = student.fullName,
                                                    homeworkId = hw.id,
                                                    homeworkTitle = hw.title,
                                                    maxScore = hw.maxScore,
                                                    current = grade,
                                                )
                                            },
                                        )
                                    }
                            }

                            if (showOrphans && displaySessions.isNotEmpty()) {
                                Spacer(Modifier.width(SessionGroupGap))
                            }
                            if (showOrphans) {
                                orphanHomeworks.forEach { hw ->
                                    val grade = gradeMap[student.id to hw.id]?.score
                                    GradeCell(
                                        grade = grade,
                                        hasSubmission = unseenSubmissionsByCell[student.id to hw.id]
                                            .orEmpty()
                                            .isNotEmpty(),
                                        editable = editable,
                                        onClick = {
                                            gradeEdit = GradeEditTarget(
                                                studentId = student.id,
                                                studentName = student.fullName,
                                                homeworkId = hw.id,
                                                homeworkTitle = hw.title,
                                                maxScore = hw.maxScore,
                                                current = grade,
                                            )
                                        },
                                    )
                                }
                            }
                        }
                    }
                }

                if (hScroll.maxValue > 0) {
                    HorizontalScrollBar(
                        scrollState = hScroll,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 10.dp, vertical = 8.dp),
                    )
                }
            }
        }
    }

    gradeEdit?.let { target ->
        GradeEditDialog(
            target = target,
            submissions = submissionsByCell[target.studentId to target.homeworkId].orEmpty(),
            canEditSubmissions = isAdmin,
            onEditSubmission = { submissionId, url -> onUpdateSubmission(submissionId, url) },
            onDeleteSubmission = { submissionId -> onDeleteSubmission(submissionId) },
            onDismiss = { gradeEdit = null },
            onSave = { score ->
                onSetGrade(target.studentId, target.homeworkId, score)
                gradeEdit = null
            },
            onClear = {
                onSetGrade(target.studentId, target.homeworkId, null)
                gradeEdit = null
            },
        )
    }

    if (showAddSession) {
        AddSessionDialog(
            onDismiss = { showAddSession = false },
            onConfirm = { date, title, description ->
                onAddSession(date, title, description)
                showAddSession = false
            },
        )
    }

    if (showAddHomework) {
        AddHomeworkDialog(
            sessions = orderedSessions,
            initialSessionId = selectedSessionId,
            onDismiss = { showAddHomework = false },
            onConfirm = { sessionId, title, description, maxScore ->
                selectedSessionId = sessionId
                onAddHomework(sessionId, title, description, maxScore)
                showAddHomework = false
            },
        )
    }

    if (showSubmissions) {
        SubmissionsDialog(
            sheet = sheet,
            editable = isAdmin,
            onDismiss = { showSubmissions = false },
            onEditSubmission = { submissionId, url -> onUpdateSubmission(submissionId, url) },
            onDeleteSubmission = { submissionId -> onDeleteSubmission(submissionId) },
        )
    }

    viewingSession?.let { session ->
        SessionDetailsDialog(
            session = session,
            editable = editable,
            onDismiss = { viewingSession = null },
            onSave = { date, title, description ->
                onUpdateSession(session.id, date, title, description)
                viewingSession = null
            },
            onDelete = {
                viewingSession = null
                val label = buildString {
                    append(formatDisplayDate(session.date))
                    if (session.title.isNotBlank()) append(" — ${session.title}")
                }
                pendingDelete = PendingDelete.Session(session.id, label)
            },
        )
    }

    viewingHomework?.let { hw ->
        HomeworkDetailsDialog(
            homework = hw,
            sessions = sheet.sessions,
            session = sheet.sessions.firstOrNull { it.id == hw.sessionId },
            editable = editable,
            isAdmin = isAdmin,
            publicToken = sheet.course.publicToken,
            formsBearerConfigured = formsBearerConfigured,
            onDismiss = { viewingHomework = null },
            onSave = { sessionId, title, description, maxScore ->
                onUpdateHomework(hw.id, sessionId, title, description, maxScore)
                viewingHomework = null
            },
            onDelete = {
                viewingHomework = null
                pendingDelete = PendingDelete.Homework(hw.id, hw.title)
            },
        )
    }

    attendanceEdit?.let { target ->
        AttendancePickerDialog(
            target = target,
            onDismiss = { attendanceEdit = null },
            onSelect = { status ->
                val key = target.studentId to target.sessionId
                localAttendance = localAttendance + (key to status)
                quickEditKey = null
                onCycleAttendance(target.studentId, target.sessionId, status)
                attendanceEdit = null
            },
            onClear = {
                val key = target.studentId to target.sessionId
                localAttendance = localAttendance + (key to null)
                quickEditKey = null
                onCycleAttendance(target.studentId, target.sessionId, null)
                attendanceEdit = null
            },
        )
    }

    pendingDelete?.let { pending ->
        val (title, message) = when (pending) {
            is PendingDelete.Session ->
                "Удалить занятие?" to "Занятие «${pending.label}» и связанные отметки посещаемости будут удалены."
            is PendingDelete.Homework ->
                "Удалить ДЗ?" to "Домашнее задание «${pending.title}» и все оценки по нему будут удалены."
        }
        ConfirmDialog(
            title = title,
            message = message,
            onConfirm = {
                when (pending) {
                    is PendingDelete.Session -> onDeleteSession(pending.id)
                    is PendingDelete.Homework -> onDeleteHomework(pending.id)
                }
                pendingDelete = null
            },
            onDismiss = { pendingDelete = null },
        )
    }
}

private fun sortSessions(
    sessions: List<LessonSessionDto>,
    order: SessionSortOrder,
): List<LessonSessionDto> = when (order) {
    SessionSortOrder.OldestFirst ->
        sessions.sortedWith(compareBy({ it.date }, { it.title }, { it.id }))
    SessionSortOrder.NewestFirst ->
        sessions.sortedWith(compareByDescending<LessonSessionDto> { it.date }
            .thenByDescending { it.title }
            .thenByDescending { it.id })
}
